import React, { useState } from "react";
import styles from "./Home.module.css";
import Panel from "./Panel";
import Avatar from "../../../components/common/Avatar";
import SegmentedControl from "../../../components/common/SegmentedControl";
import WeekPager from "../../../components/common/WeekPager";
import { cellTone, sortCapacity, weekRangeLabel } from "./homeData";
import { dayName, formatDay, toISODate } from "../../../utils/weekDates";

const KEYS = [["under", "#dbeafe", "Under 6h"], ["ok", "#dcfce7", "6–8h"], ["over", "#fee2e2", "Over 8h"], ["leave", "#e2e8f0", "Leave"]];
const round = (n) => Math.round(n * 10) / 10;

// One row per person: hours per working day, booked against capacity and logged so far.
export default function TeamCapacity({ people, weekStart, onWeekChange, onOpenPerson }) {
  const [order, setOrder] = useState("booked");
  const rows = sortCapacity(people, order);
  const today = toISODate(new Date());
  const dates = people[0]?.days.map((d) => d.date) || [];

  return (
    <Panel
      title="Team capacity"
      count={rows.length || null}
      countTone="neutral"
      tools={
        <>
          <WeekPager weekStart={weekStart} label={weekRangeLabel(weekStart)} onChange={onWeekChange} />
          <div className={styles.keys}>{KEYS.map(([k, c, label]) => <span key={k}><i style={{ background: c }} />{label}</span>)}</div>
          <SegmentedControl ariaLabel="Sort people" value={order} onChange={setOrder} options={[{ value: "booked", label: "Most booked" }, { value: "name", label: "A–Z" }]} />
        </>
      }
    >
      {rows.length === 0 ? (
        <div className={styles.empty}>No people to show.</div>
      ) : (
        <div className={styles.capacity}>
          <div className={`${styles.capGrid} ${rows.length > 5 ? styles.capScroll : ""}`}>
            <div className={`${styles.capHead} ${styles.capHeadLeft}`}>Person</div>
            {dates.map((d) => (
              <div key={d} className={`${styles.capHead} ${d === today ? styles.capToday : ""}`}>{dayName(d).toUpperCase()} {d.slice(8)}</div>
            ))}
            <div className={`${styles.capHead} ${styles.capHeadLeft}`}>Booked / capacity</div>

            {rows.map(({ person, booked, capacity }) => {
              const diff = round(booked - capacity);
              return (
                <React.Fragment key={person.employee_id}>
                  <div className={styles.person}>
                    <Avatar name={person.name} imageUrl={person.avatar_url} round />
                    <div style={{ minWidth: 0 }}>
                      <div className={styles.personName}><button type="button" title="Open on Schedule" onClick={() => onOpenPerson(person)}>{person.name}</button></div>
                      <div className={styles.personRole}>{person.job_title || "Team member"}</div>
                    </div>
                  </div>
                  {person.days.map((d) => {
                    const tone = cellTone(d.booked_hours, person.daily_capacity, d.on_leave);
                    return (
                      <div key={d.date} className={`${styles.cell} ${styles[`cell_${tone}`]} ${d.date === today ? styles.cellToday : ""}`} title={`${formatDay(d.date)}: ${round(d.booked_hours)}h of ${round(person.daily_capacity)}h`}>
                        {tone === "leave" ? "Leave" : tone === "none" ? "–" : `${round(d.booked_hours)}h`}
                      </div>
                    );
                  })}
                  <div className={styles.booked}>
                    <div className={styles.bookedRow}>
                      <span>{round(booked)} / {round(capacity)}h</span>
                      {diff > 0 && <span className={styles.over}>+{diff}h over</span>}
                      {diff < 0 && booked < capacity / 2 && <span className={styles.free}>{-diff}h free</span>}
                    </div>
                    <div className={styles.track}><div className={styles.trackFill} style={{ width: `${Math.min(100, capacity ? (booked / capacity) * 100 : 0)}%`, background: diff > 0 ? "#dc2626" : undefined }} /></div>
                    <div className={styles.logged}>Logged {round(person.logged_hours)}h so far</div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </Panel>
  );
}
